import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, Form, Input, Layout, Typography } from "antd";
import { ScanOutlined } from "@ant-design/icons";

import { ApiError, api } from "../api/client";
import { BRAND_365_GRADIENT, FitLine } from "../components/brand";
import { CameraScanModal } from "../components/CameraScanModal";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner";
import { cameraScanSupported } from "../utils/cameraScan";
import { useDarkMode } from "../theme/useDarkMode";

const { Text } = Typography;

interface LoginFormValues {
  username: string;
  password: string;
}

/** Turn a login failure into an honest message. A real bad password is a
 *  401/403; a 5xx — or a flat-out unreachable server, e.g. while the backend
 *  restarts — must NOT read as "Invalid credentials". */
function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return "Invalid credentials";
    if (err.status >= 500)
      return "Server unavailable — it may be restarting. Try again in a moment.";
    return err.detail || "Sign-in failed. Please try again.";
  }
  // fetch rejected outright (connection refused) — server unreachable.
  return "Can't reach the server — it may be restarting. Try again in a moment.";
}

export const LoginPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const { mode } = useDarkMode();
  const grad =
    mode === "dark" ? BRAND_365_GRADIENT.dark : BRAND_365_GRADIENT.light;

  useEffect(() => {
    api
      .oidcStatus()
      .then((s) => setOidcEnabled(s.enabled))
      .catch(() => setOidcEnabled(false));
  }, []);

  const onBarcode = useCallback(
    async (barcode: string) => {
      setError(null);
      setSubmitting(true);
      try {
        await api.loginBarcode(barcode);
        navigate("/dashboard");
      } catch (err) {
        setError(loginErrorMessage(err));
      } finally {
        setSubmitting(false);
      }
    },
    [navigate],
  );

  const onCameraDetected = useCallback(
    (code: string) => {
      setScanOpen(false);
      void onBarcode(code);
    },
    [onBarcode],
  );

  useBarcodeScanner(onBarcode, !submitting);

  const onFinish = async (values: LoginFormValues) => {
    setError(null);
    setSubmitting(true);
    try {
      await api.loginPassword(values.username, values.password);
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof ApiError ? "Invalid credentials" : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout
      style={{
        minHeight: "100dvh",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--ant-color-bg-layout)",
      }}
    >
      <Card style={{ width: 380 }}>
        <div
          style={{
            width: 190,
            maxWidth: "70%",
            margin: "0 auto 12px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <FitLine
            text="strichliste"
            weight={700}
            fill="var(--ant-color-text, currentColor)"
          />
          <FitLine text="365" weight={900} gradient={grad} />
        </div>
        <Text type="secondary" style={{ display: "block", textAlign: "center" }}>
          Sign in to continue. Barcode scans are accepted at any time.
        </Text>
        {error && <Alert type="error" message={error} style={{ marginTop: 16 }} showIcon />}
        <Form<LoginFormValues>
          layout="vertical"
          onFinish={onFinish}
          disabled={submitting}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: "Username is required" }]}
          >
            <Input autoComplete="username" autoFocus />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              Sign in
            </Button>
          </Form.Item>
        </Form>
        {cameraScanSupported && (
          <Button
            block
            icon={<ScanOutlined />}
            disabled={submitting}
            onClick={() => {
              setError(null);
              setScanOpen(true);
            }}
            style={{ marginBottom: 8 }}
          >
            Scan barcode with camera
          </Button>
        )}
        {oidcEnabled && (
          <Button
            block
            onClick={() => {
              window.location.href = "/api/auth/oidc/login";
            }}
          >
            Sign in with SSO
          </Button>
        )}
        <CameraScanModal
          open={scanOpen}
          onClose={() => setScanOpen(false)}
          onDetected={onCameraDetected}
          onError={setError}
          hint="Point the rear camera at your login barcode."
        />
      </Card>
    </Layout>
  );
};
